import PropTypes from "prop-types";
import { Col, Form, Nav } from "react-bootstrap";
import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "react-bootstrap-icons";

export function Pager(props) {
  const [pageInput, setPageInput] = useState(props.page);

  useEffect(() => {
    setPageInput(props.page);
  }, [props.page]);

  const handlePageSizeChange = (event) => {
    let newPageSize = Number(event.target.value);
    props.onPageSizeChange(props.page, newPageSize);
  };

  const handlePageChange = (value) => {
    const newPage = getValidPageValue(value);
    props.onPageChange(newPage, props.pageSize);
  };

  const handlePageInputBlur = () => {
    handlePageChange(Number(pageInput));
  };

  const handlePageInputKeyDown = (e) => {
    if (e.key === "Enter") {
      handlePageChange(Number(pageInput));
      e.target.blur();
    }
  };

  const handlePreviousPage = () => {
    handlePageChange(props.page - 1);
  };

  const handleNextPage = () => {
    handlePageChange(props.page + 1);
  };

  const getValidPageValue = (pageValue) => {
    return clamp(1, pageValue, props.maxPages);
  };

  const clamp = (min, value, max) => {
    return Math.max(Math.min(max, value), min);
  };

  const isDisabled = props.disabled || props.isLoading;

  const startRecord =
    props.maxItems === 0 ? 0 : (props.page - 1) * props.pageSize + 1;
  const endRecord = Math.min(props.page * props.pageSize, props.maxItems);

  if (props.variant === "modern") {
    return (
      <Nav
        aria-label="Page navigation row"
        className="pager-nav align-items-center"
      >
        <Col
          xs={12}
          sm={3}
          className="form-group text-start d-flex align-items-center gap-2 mb-2 mb-sm-0"
        >
          <label className="inline text-nowrap pager-label">Go to page:</label>
          <Form.Control
            type="number"
            min={1}
            max={props.maxPages}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onBlur={handlePageInputBlur}
            onKeyDown={handlePageInputKeyDown}
            disabled={isDisabled}
            className="form-control page-select wide-input pager-input"
          />
        </Col>

        <Col
          xs={12}
          sm={3}
          className="form-group text-start text-sm-center d-flex align-items-center justify-content-sm-center gap-2 mb-2 mb-sm-0"
        >
          <label className="inline text-nowrap pager-label">
            Rows per page:
          </label>
          <Form.Select
            onChange={handlePageSizeChange}
            value={props.pageSize}
            disabled={isDisabled}
            className="form-control page-select wide-input pager-select"
          >
            {props.pageSizeOptions.map((s) => (
              <option value={s} key={s}>
                {s}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col
          xs={6}
          sm={3}
          className="pagination-counts text-start text-sm-center align-self-center"
        >
          <span className="current-page">
            {startRecord} - {endRecord}
          </span>{" "}
          of <span className="total-count">{props.maxItems}</span>
        </Col>

        <Col
          xs={6}
          sm={3}
          className="d-flex justify-content-end align-items-center"
        >
          <div className="pagination-buttons d-flex gap-1 align-items-center">
            <button
              type="button"
              className="pager-btn"
              onClick={handlePreviousPage}
              disabled={isDisabled || props.page <= 1}
              aria-label="Previous page"
              title="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              className="pager-btn"
              onClick={handleNextPage}
              disabled={isDisabled || props.page >= props.maxPages}
              aria-label="Next page"
              title="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </Col>
      </Nav>
    );
  }

  return (
    <Nav aria-label="Page navigation row">
      <Col xs={6} sm={3} className="form-group text-start">
        <label className="inline">Go to page:</label>
        <Form.Control
          type="number"
          min={1}
          max={props.maxPages}
          value={pageInput}
          onChange={(e) => setPageInput(e.target.value)}
          onBlur={handlePageInputBlur}
          onKeyDown={handlePageInputKeyDown}
          disabled={isDisabled}
          className="form-control page-select wide-input"
        />
      </Col>

      <Col xs={6} sm={3} className="form-group text-end">
        <label className="inline">Rows per page:</label>
        <Form.Select
          onChange={handlePageSizeChange}
          value={props.pageSize}
          disabled={isDisabled}
          className="form-control page-select wide-input"
        >
          {props.pageSizeOptions.map((s) => (
            <option value={s} key={s}>
              {s}
            </option>
          ))}
        </Form.Select>
      </Col>

      <Col xs={6} sm={3} className="hidden-xs pagination-counts text-end">
        <span className="current-page">
          {startRecord} - {endRecord}
        </span>{" "}
        of <span className="total-count">{props.maxItems}</span>
      </Col>

      <Col xs={6} sm={3}>
        <ul className="pagination">
          <li>
            <button
              type="button"
              onClick={handleNextPage}
              hidden={props.page === props.maxPages}
              disabled={isDisabled}
            >
              &gt;
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={handlePreviousPage}
              hidden={props.page === 1}
              disabled={isDisabled}
            >
              &lt;
            </button>
          </li>
        </ul>
      </Col>
    </Nav>
  );
}

Pager.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  maxPages: PropTypes.number.isRequired,
  maxItems: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  pageSizeOptions: PropTypes.array,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  variant: PropTypes.string,
};

Pager.defaultProps = {
  pageSizeOptions: [10, 25, 50],
};
